import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, Button, Menu, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import format from 'date-fns/format';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DateFormatsEnum } from '../../../constants/date';
import { DEFAULT_PAGE_NUMBER, RowsPerPageEnum } from '../../../constants/oneLink';

import { routes, URL_VARS } from '../../../constants/routing';
import { DEPT_ADMIN } from '../../../constants/users';
import { useGetEngagements } from '../../../service/EngagementService';
import { toggleDownloadSuccessModal } from '../../../store/actions/modals';
import { downloadReportsCSVRequest } from '../../../store/actions/reports';
import { selectAdminType } from '../../../store/selectors/auth';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { IDownloadReportsCSVRequestPayload, IReport, ReportTypesEnum } from '../../../types/reports';
import { IApiError, IDownloadCSVSuccessPayload, NotificationListEnum } from '../../../types/shell';
import notification from '../../../utils/notification';
import { getReportStatusAndDate } from '../../../utils/reports';
import { hasPermission } from '../../../utils/users';
import { ReportsStatus } from '../../index';
import { OrderLinkButton } from '../../_mui/_controls_';
import { ITableColumn, TableMUI } from '../../_mui/_tables_/Table';
import { Section } from './common';

import variables from '../../../_variables.scss';

interface IProps {
  instance?: IOneLinkWithCampaignSummaries | null;
}

export const ConfirmedSendsSection: React.FC<IProps> = ({ instance }) => {
  const theme = useTheme();
  const adminType = useSelector(selectAdminType);
  const [searchValue, setSearchValue] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(RowsPerPageEnum.Ten);
  const { enabled_2fa, recipients_count, max_submit_count, use_times_count } = instance ?? {};

  const { data, isFetching } = useGetEngagements({
    page,
    page_size: pageSize,
    ...(query && { search_query: query }),
    ...(instance?.uid && { one_link_id: instance.uid }),
  });
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setQuery(searchValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const tableConfig: ITableColumn<IReport>[] = [
    {
      label: 'Recipient',
      render: ({ receiver_first_name, receiver_last_name }: IReport) => (
        <Typography variant="body2">
          {receiver_first_name} {receiver_last_name}
        </Typography>
      ),
    },
    {
      label: 'Status',
      render: (engagement: IReport) => {
        const { label: statusLabel, color: statusColor } = getReportStatusAndDate(engagement);
        return <ReportsStatus status={statusLabel} backgroundColor={statusColor} tiny />;
      },
    },
    {
      label: 'Order ID',
      render: ({ uid, order_id }: IReport) => (
        <OrderLinkButton
          orderId={order_id}
          reportUrl={routes.reports.getReportsUrl({
            reportId: uid,
            type: ReportTypesEnum.Regular,
            flowId: URL_VARS.SUMMARY,
          })}
        />
      ),
    },
    {
      label: 'Submitted Date',
      render: (engagement: IReport) => {
        const { createdAtDate } = getReportStatusAndDate(engagement);
        return <Typography variant="body2">{format(new Date(createdAtDate), DateFormatsEnum.ddMMMyyyy)}</Typography>;
      },
    },
  ];

  const handleSetPerPage = useCallback((rowCount: number) => {
    setPage(DEFAULT_PAGE_NUMBER);
    setPageSize(rowCount);
  }, []);

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(DEFAULT_PAGE_NUMBER);
  }, []);

  const pageOptions = [RowsPerPageEnum.Ten, RowsPerPageEnum.Twenty, RowsPerPageEnum.Fifty];

  const downloadReportsCSV = React.useCallback(
    (payload?: Partial<IDownloadReportsCSVRequestPayload>) => {
      dispatch(
        downloadReportsCSVRequest({
          ...(hasPermission([DEPT_ADMIN], adminType) ? { isDeptSearch: true } : { isOrgSearch: true }),
          type: ReportTypesEnum.Regular,
          one_link_id: instance?.uid,
          ...payload,
        }),
      );
    },
    [adminType, dispatch, instance?.uid],
  );

  const handleDownloadClick = React.useCallback(
    (inventoryBreakdown: boolean) => {
      const downloadReportsCSVPromise = new Promise<IDownloadCSVSuccessPayload>((resolve, reject) => {
        downloadReportsCSV({ resolve, reject, inventory_breakdown: inventoryBreakdown, one_link_id: instance?.uid });
      }).then((response: IDownloadCSVSuccessPayload) => {
        dispatch(toggleDownloadSuccessModal(response));
      });

      notification.promise<void | IApiError>(NotificationListEnum.DownloadData, {
        promise: downloadReportsCSVPromise,
        promiseParams: {
          pending: 'Downloading process started. We’ll notify you when the file is ready!',
          error: {
            render: ({ data: error }) =>
              (error && error.message) || "Something bad has happened. CSV file wasn't created",
          },
        },
      });
    },
    [downloadReportsCSV, dispatch, instance?.uid],
  );

  const handleCampaignDownload = useCallback(
    (inventoryBreakdown: boolean) => {
      handleDownloadClick(inventoryBreakdown);
      handleClose();
    },
    [handleDownloadClick, handleClose],
  );

  const sendsLeftToConfirm = useMemo(() => {
    const twoFAEnabledOnly = enabled_2fa && !max_submit_count;
    const redemptionLimitOnly = !enabled_2fa && !!max_submit_count;
    const twoFAWithRedemptionLimit = enabled_2fa && !!max_submit_count;

    const maxSubmitCount = max_submit_count ?? 0;
    const recipientsCount = recipients_count ?? 0;
    const useTimesCount = use_times_count ?? 0;

    switch (true) {
      case twoFAWithRedemptionLimit: {
        const minLimit = Math.min(recipientsCount, maxSubmitCount);
        return minLimit - useTimesCount;
      }
      case twoFAEnabledOnly:
        return recipientsCount - useTimesCount;
      case redemptionLimitOnly:
        return maxSubmitCount - useTimesCount;
      default:
        return 0;
    }
  }, [enabled_2fa, recipients_count, max_submit_count, use_times_count]);

  const boxStyle = { fontWeight: theme.typography.fontWeightRegular, display: 'flex' };

  const RenderStatus = useCallback(
    ({ backgroundColor, label, value }: { backgroundColor: string; label: string; value?: number }) => (
      <Box style={boxStyle}>
        <ReportsStatus backgroundColor={backgroundColor} />
        {label}: <Typography style={{ marginLeft: 5 }}>{value}</Typography>
      </Box>
    ),
    [],
  );

  const renderTitle = useCallback(() => {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        Sends:
        <RenderStatus backgroundColor={variables.budgetGreen} label="Confirmed" value={use_times_count} />
        {(!!enabled_2fa || max_submit_count) && (
          <RenderStatus backgroundColor={variables.orangeSun} label="Left" value={sendsLeftToConfirm} />
        )}
      </Box>
    );
  }, [use_times_count, sendsLeftToConfirm, enabled_2fa, max_submit_count]);

  const renderCSVMenuButton = useCallback(() => {
    return (
      <>
        <Button onClick={handleClick}>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <SaveAltIcon />
            <Typography variant="body2">CSV</Typography>
          </Box>
        </Button>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
          <MenuItem onClick={() => handleCampaignDownload(false)}>Campaign Report</MenuItem>
          <MenuItem onClick={() => handleCampaignDownload(true)}>Product Report</MenuItem>
        </Menu>
      </>
    );
  }, [handleCampaignDownload, handleClose, handleClick, anchorEl]);

  return (
    <Section
      title={
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <>
            {renderTitle()}
            {renderCSVMenuButton()}
          </>
        </Box>
      }
      minHeight={600}
    >
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        value={searchValue}
        onChange={handleSearch}
        sx={{ mb: 2 }}
        disabled={isFetching}
        inputRef={(ref) => {
          if (ref && searchValue) {
            ref.focus();
          }
        }}
      />
      <TableMUI
        containerStyle={{ maxHeight: 440, overflow: 'auto', width: '100%' }}
        totalCount={data?.total_count || 0}
        data={data?.engagements}
        columns={tableConfig}
        page={page}
        rowsPerPage={pageSize}
        onPageChange={setPage}
        onRowsPerPageChange={handleSetPerPage}
        isLoading={isFetching}
        perPageOptions={pageOptions}
        emptyPlaceholderTitle="No confirmed sends yet"
        emptyPlaceholderSubtitle="Waiting for information"
      />
    </Section>
  );
};
